// Cognito Hosted-UI auth config (OIDC authorization-code + PKCE via
// react-oidc-context). All values come from the build-time VITE env; when the
// authority/client are absent (local dev) auth is disabled and the app runs
// open against a backend started with INPACT_AUTH_DISABLED=1.
import type { AuthProviderNoUserManagerProps } from "react-oidc-context";
import type { AuthUser } from "../types/inpact";

const authority = import.meta.env.VITE_COGNITO_AUTHORITY as string | undefined;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined;
const domain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
const identityProvider = (import.meta.env.VITE_COGNITO_IDP as string | undefined)?.trim();
const explicitSignOutKey = "inpact:explicit-sign-out";

function redirectUri(): string {
  // The Docker build writes every VITE var into .env, so an omitted optional
  // override arrives as "" (not undefined) — `??` wouldn't fall back. Use a
  // truthy check so the default is window.origin, not a blank redirect URI.
  const override = (import.meta.env.VITE_COGNITO_REDIRECT_URI as string | undefined)?.trim();
  return override || `${window.location.origin}/`;
}

// The Hosted-UI domain is REQUIRED, not optional: without it logout can only
// drop the local session while the Cognito SSO session lives on, so the
// auto-redirect signs the user straight back in. Enabling auth without a
// working logout or the required staff IdP is worse than not enabling it, so
// all four must be present.
export const cognitoEnabled = Boolean(authority && clientId && domain && identityProvider);

// A deploy that sets SOME but not all four values is a misconfiguration. We
// must NOT silently fall back to the dev passthrough (the SPA would run with no
// login — and if VITE_INPACT_API_KEY is also baked, it stays usable, defeating
// the rollout). Fail closed: the gate shows a config error instead.
export const cognitoMisconfigured =
  Boolean(authority || clientId || domain || identityProvider) && !cognitoEnabled;

export const oidcConfig: AuthProviderNoUserManagerProps = {
  authority: authority ?? "",
  client_id: clientId ?? "",
  redirect_uri: cognitoEnabled ? redirectUri() : "",
  response_type: "code",
  scope: "openid email profile",
  extraQueryParams: identityProvider
    ? { identity_provider: identityProvider }
    : undefined,
  // Strip ?code=&state= from the URL once the code exchange completes.
  onSigninCallback: () => {
    clearExplicitSignOut();
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

/** Cognito Hosted-UI logout endpoint (its OIDC end_session is non-standard). */
export function cognitoLogoutUrl(): string | null {
  if (!domain || !clientId) return null;
  return `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(redirectUri())}`;
}

/** Use the ID token because it carries the mapped staff email claim. */
export function apiBearerToken(user: {
  id_token?: string;
  access_token?: string;
}): string | undefined {
  return user.id_token;
}

export function explicitlySignedOut(): boolean {
  return window.localStorage.getItem(explicitSignOutKey) === "1";
}

export function markExplicitSignOut(): void {
  window.localStorage.setItem(explicitSignOutKey, "1");
}

export function clearExplicitSignOut(): void {
  window.localStorage.removeItem(explicitSignOutKey);
}

export function subscribeToExplicitSignOut(listener: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === explicitSignOutKey && event.newValue === "1") listener();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** "jane.q.doe@example.test" → "JD" (first letter of first + last name part). */
export function initialsFromEmail(email: string): string {
  const local = email.split("@", 1)[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = parts.length >= 2
    ? parts[0][0] + parts[parts.length - 1][0]
    : local.slice(0, 2);
  return letters.toUpperCase();
}

/** Map an OIDC profile to the app's AuthUser. Everyone is staff in v1. */
export function authUserFromProfile(profile: { email?: string }): AuthUser | null {
  const email = profile.email;
  if (!email) return null;
  return { email, role: "staff", initials: initialsFromEmail(email) };
}
